import { useRef, useState, useMemo, useEffect } from "react";
import {
  designCreateTemplate, designAiCompose, designGetDraft,
  registerGalleryImage, aiGetOpenAiKey, getAiPoints, formsList, formEmbedPreview,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import ImageUpload from "@/components/ImageUpload";
import {
  Loader2, ChevronLeft, Sparkles, PenLine, Save, Wand2, Eye,
} from "lucide-react";
import { toast } from "sonner";

const IMG_TOKEN = /\{\{\s*image\s*:\s*(https?:\/\/[^\s}]+)\s*\}\}/gi;

// Client-side approximation of the server body composer (preview only).
function composeBodyPreview(text) {
  const raw = (text || "").trim();
  if (!raw) return "";
  return raw
    .split(/\n\s*\n/)
    .map((block) => {
      const b = block.trim();
      if (!b) return "";
      if (/^\{\{\s*FORM_(LINK|BUTTON|EMBED)\s*:\s*[^}\s]+\s*\}\}$/i.test(b)) return b;
      const only = b.match(/^\{\{\s*image\s*:\s*(https?:\/\/[^\s}]+)\s*\}\}$/i);
      if (only) {
        return `<p style="margin:20px 0;text-align:center;"><img src="${only[1]}" style="max-width:100%;height:auto;border-radius:10px;" /></p>`;
      }
      const html = b
        .replace(IMG_TOKEN, (_, u) => `<br><img src="${u}" style="max-width:100%;height:auto;border-radius:10px;margin:12px 0;" /><br>`)
        .replace(/\n/g, "<br>");
      return `<p style="margin:0 0 16px;font-family:Arial,Helvetica,sans-serif;font-size:16px;line-height:1.6;color:#1f2937;">${html}</p>`;
    })
    .join("");
}

function wrapPreview(bodyHtml, footer) {
  const header = `<tr><td style="padding:14px 20px;"><img src="https://uploads.orevital.com/postcue/header-light.png" width="600" alt="OreVital — Let's Talk Peptides" style="display:block;width:100%;max-width:600px;height:auto;border:0;" /></td></tr>`;
  const footerRow = footer
    ? `<tr><td style="padding:0;">${footer}</td></tr>`
    : `<tr><td style="background:#f3f4f6;padding:20px 28px;text-align:center;font-family:Arial;font-size:12px;color:#9ca3af;">The standard OreVital footer + unsubscribe is added automatically when you send.</td></tr>`;
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f6f4ef;font-family:Arial;"><tr><td align="center" style="padding:8px 4px;"><table role="presentation" width="640" cellpadding="0" cellspacing="0" style="max-width:640px;width:100%;background:#fff;border-collapse:collapse;">${header}<tr><td style="padding:0;">${bodyHtml}</td></tr>${footerRow}</table></td></tr></table>`;
}

export default function EmailDesigner({ userRecordId, editTemplateId, footerHtml, onCancel, onDone }) {
  const [title, setTitle] = useState("");
  const [subject, setSubject] = useState("");
  const [mode, setMode] = useState("write"); // write | ai
  const [content, setContent] = useState("");
  const [aiHtml, setAiHtml] = useState(""); // AI-polished body html
  const [polishing, setPolishing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [forms, setForms] = useState([]);
  useEffect(() => { formsList(userRecordId).then((r) => setForms((r.forms || []).filter((f) => f.active && f.form_id))).catch(() => setForms([])); }, [userRecordId]);
  // Server-rendered flyer-style form blocks for the preview, keyed by form ref
  const [formPreviews, setFormPreviews] = useState({});
  const formRefs = useMemo(() => {
    const refs = new Set();
    for (const m of `${content || ""}\n${aiHtml || ""}`.matchAll(/\{\{\s*FORM_EMBED\s*:\s*([^}\s]+)\s*\}\}/gi)) refs.add(m[1].toLowerCase());
    return [...refs];
  }, [content, aiHtml]);
  useEffect(() => {
    formRefs.filter((ref) => !(ref in formPreviews)).forEach((ref) => {
      formEmbedPreview(userRecordId, ref)
        .then((r) => setFormPreviews((p) => ({ ...p, [ref]: r.html || "" })))
        .catch(() => setFormPreviews((p) => ({ ...p, [ref]: "" })));
    });
  }, [formRefs, userRecordId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [loadingDraft, setLoadingDraft] = useState(!!editTemplateId);
  const [provider, setProvider] = useState("emergent");
  const [openaiConnected, setOpenaiConnected] = useState(false);
  const [points, setPoints] = useState(null);
  const [urls, setUrls] = useState(["", "", ""]);
  const taRef = useRef(null);

  useEffect(() => {
    aiGetOpenAiKey(userRecordId).then((r) => { setOpenaiConnected(!!r.connected); if (r.default_provider) setProvider(r.default_provider); }).catch(() => {});
    getAiPoints(userRecordId).then((r) => setPoints(r)).catch(() => {});
  }, [userRecordId]);

  useEffect(() => {
    if (!editTemplateId) return;
    let alive = true;
    designGetDraft(editTemplateId, userRecordId)
      .then((d) => {
        if (!alive) return;
        setTitle(d.title || "");
        setSubject(d.subject || "");
        if (d.has_draft) {
          setContent(d.content || "");
          setMode(d.mode || "write");
          if ((d.mode || "") === "ai") setAiHtml(d.body_html || "");
        } else {
          toast.message("This email was built outside the designer — you can retype it here.");
        }
      })
      .catch(() => toast.error("Could not load this email for editing"))
      .finally(() => alive && setLoadingDraft(false));
    return () => { alive = false; };
  }, [editTemplateId, userRecordId]);

  function insertAtCursor(text) {
    const ta = taRef.current;
    if (!ta) { setContent((c) => c + text); return; }
    const start = ta.selectionStart ?? content.length;
    const end = ta.selectionEnd ?? content.length;
    const next = content.slice(0, start) + text + content.slice(end);
    setContent(next);
    setAiHtml("");
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + text.length;
      ta.setSelectionRange(pos, pos);
    });
  }

  async function handlePickImage(url) {
    if (!url) return;
    const count = (content.match(/\{\{\s*image\s*:/gi) || []).length;
    if (count >= 3) { toast.error("You can add up to 3 images per email"); return; }
    insertAtCursor(`\n\n{{image:${url}}}\n\n`);
    try {
      await registerGalleryImage(userRecordId, url, "Email image");
    } catch { /* non-blocking */ }
    toast.success("Image added");
  }

  async function polish() {
    const cleanUrls = urls.map((u) => u.trim()).filter(Boolean);
    if (!content.trim() && cleanUrls.length === 0) { toast.error("Write notes or add a reference URL first"); return; }
    const cost = 1 + cleanUrls.length;
    const bal = points?.balance ?? 0;
    if (bal < cost) { toast.error(`Not enough AI points — needs ${cost}, you have ${bal}. You earn more as your sales grow.`); return; }
    if (!window.confirm(`This will use ${cost} AI point${cost > 1 ? "s" : ""} (${cleanUrls.length ? `1 to write + ${cleanUrls.length} for links` : "to write"}). You have ${bal}. Continue?`)) return;
    setPolishing(true);
    try {
      const res = await designAiCompose({ user_record_id: userRecordId, content, provider, urls: cleanUrls });
      setAiHtml(res.body_html || "");
      if (res.points) setPoints(res.points);
      toast.success("AI drafted your email — check the preview");
    } catch (err) {
      toast.error(err?.response?.data?.detail || (!err?.response ? "The AI took too long or the connection dropped — please try again" : "AI could not compose the email — please try again"));
    } finally {
      setPolishing(false);
    }
  }

  async function save() {
    if (!title.trim()) { toast.error("Give your email a title"); return; }
    if (mode === "write" && !content.trim()) { toast.error("Add some content"); return; }
    if (mode === "ai" && !aiHtml && !content.trim()) { toast.error("Write notes and tap Polish with AI"); return; }
    setSaving(true);
    try {
      const payload = { user_record_id: userRecordId, title, subject, content, mode };
      if (editTemplateId) payload.template_id = editTemplateId;
      if (mode === "ai" && aiHtml) payload.body_html = aiHtml;
      await designCreateTemplate(payload);
      toast.success(editTemplateId ? "Email updated" : "Email saved — find it in your templates");
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save email");
    } finally {
      setSaving(false);
    }
  }

  const previewHtml = useMemo(() => {
    let body;
    if (mode === "ai" && aiHtml) {
      body = aiHtml;
    } else {
      const composed = composeBodyPreview(content);
      body = composed ? `<div style="padding:28px 30px;">${composed}</div>` : "";
    }
    if (body) {
      const ctaBtn = (label) => `<a href="#cta" style="display:inline-block;background:#0b2545;color:#fff;text-decoration:none;padding:14px 28px;border-radius:999px;font-weight:bold;font-family:Arial,sans-serif;font-size:15px;">${label}</a>`;
      body = body.replace(/\{\{\s*APPROVE_BUTTON\s*\}\}/gi, ctaBtn("Yes — approve me")).replace(/\{\{\s*PROSPECT_BUTTON\s*\}\}/gi, ctaBtn("Yes — I'm interested")).replace(/\{\{\s*(APPROVE|PROSPECT)_URL\s*\}\}/gi, "#cta");
      body = body.replace(/\{\{\s*FORM_(LINK|BUTTON|EMBED)\s*:\s*([^}\s]+)\s*\}\}/gi, (_, kind, ref) => {
        const k = kind.toUpperCase();
        const btn = `<a href="#form-preview" style="display:inline-block;background:#0b2545;color:#fff;text-decoration:none;padding:14px 28px;border-radius:999px;font-weight:bold;font-family:Arial,sans-serif;font-size:15px;">Open my form</a>`;
        if (k === "LINK") return "https://orevital.com/form/…personal-link…";
        if (k === "BUTTON") return btn;
        const real = formPreviews[ref.toLowerCase()];
        if (real) return real;
        return `<div style="font-family:Arial,sans-serif;border:1px dashed #d6ad52;border-radius:16px;padding:22px 20px;margin:18px 0;text-align:center;color:#6b7280;font-size:13px;">${real === "" ? `Form ${ref} not found` : "Loading form preview…"}</div>`;
      });
    }
    return wrapPreview(body || "<div style='padding:28px'><p style='color:#9ca3af;font-family:Arial'>Your email preview will appear here…</p></div>", footerHtml);
  }, [mode, aiHtml, content, footerHtml, formPreviews]);

  return (
    <div data-testid="email-designer">
      <button
        onClick={onCancel}
        className="flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900 mb-3"
        data-testid="designer-back-btn"
      >
        <ChevronLeft size={16} /> All templates
      </button>

      <div className="rounded-2xl bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white p-5 mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2"><Wand2 size={18} /> {editTemplateId ? "Edit email" : "Create email"}</h3>
        <p className="text-sm text-white/85">
          Type your message, add images, and we'll wrap it in the branded OreVital header &amp; footer.
        </p>
      </div>

      {loadingDraft ? (
        <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-zinc-400" /></div>
      ) : (
      <div className="space-y-4">
        <div>
          <div className="text-xs font-medium text-zinc-600 mb-1">Title (internal name)</div>
          <Input value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Spring peptide promo" className="h-10 rounded-lg"
            data-testid="designer-title" />
        </div>
        <div>
          <div className="text-xs font-medium text-zinc-600 mb-1">Email subject line</div>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)}
            placeholder="e.g. A little something for you, {{first_name}}" className="h-10 rounded-lg"
            data-testid="designer-subject" />
        </div>

        <div className="flex rounded-lg bg-zinc-100 p-0.5 w-fit">
          <button onClick={() => setMode("write")}
            className={`px-3 h-8 rounded-md text-xs font-medium flex items-center gap-1 ${mode === "write" ? "bg-white shadow-sm text-zinc-900" : "text-zinc-500"}`}
            data-testid="designer-mode-write">
            <PenLine size={13} /> Write it myself
          </button>
          <button onClick={() => setMode("ai")}
            className={`px-3 h-8 rounded-md text-xs font-medium flex items-center gap-1 ${mode === "ai" ? "bg-white shadow-sm text-zinc-900" : "text-zinc-500"}`}
            data-testid="designer-mode-ai">
            <Sparkles size={13} /> Let AI polish it
          </button>
        </div>

        <div>
          <div className="text-xs font-medium text-zinc-600 mb-1 flex items-center justify-between">
            <span>{mode === "ai" ? "Your rough notes / bullet points" : "Your message"}</span>
            <span className="text-[10px] text-zinc-400">Use {"{{first_name}}"} for personalization</span>
          </div>
          <Textarea ref={taRef} value={content}
            onChange={(e) => { setContent(e.target.value); setAiHtml(""); }}
            rows={9} className="rounded-lg text-sm"
            placeholder={mode === "ai"
              ? "e.g. announce our new NAD+ roller, 15% off this week, book a facial…"
              : "Write your email here. Leave a blank line between paragraphs."}
            data-testid="designer-content" />
          <div className="mt-2">
            <div className="text-[11px] font-medium text-zinc-500 mb-1">
              Insert a variable (auto-fills per recipient)
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="designer-variables">
              {[
                { label: "First name", token: "{{first_name}}" },
                { label: "Full name", token: "{{full_name}}" },
                { label: "Company", token: "{{customer_company}}" },
                { label: "Total revenue", token: "{{total_revenue}}" },
                { label: "Last order (items + total)", token: "{{LAST_ORDER}}" },
                { label: "Order #", token: "{{ORDER_NUMBER}}" },
                { label: "Order status", token: "{{ORDER_STATUS}}" },
                { label: "Order items (photo · price · MSRP)", token: "{{ORDER_ITEMS}}" },
                { label: "Review buttons (per product · earn rewards)", token: "{{REVIEW_ITEMS}}" },
                { label: "Order total", token: "{{ORDER_TOTAL}}" },
                { label: "Shipping address", token: "{{SHIPPING_ADDRESS}}" },
                { label: "Billing address", token: "{{BILLING_ADDRESS}}" },
                { label: "Weight", token: "{{ORDER_WEIGHT}}" },
                { label: "Ambassador line", token: "{{AMBASSADOR_LINE}}" },
                { label: "CTA: Approve me button (sets approved = 1)", token: "{{APPROVE_BUTTON}}" },
                { label: "CTA: I'm interested button (sets prospect = Y)", token: "{{PROSPECT_BUTTON}}" },
                { label: "CTA approve link (URL only)", token: "{{APPROVE_URL}}" },
                { label: "CTA interested link (URL only)", token: "{{PROSPECT_URL}}" },
                { label: "Return policy", token: "{{RETURN_POLICY}}" },
                { label: "Tracking link", token: "{{TRACKING_URL}}" },
                { label: "Tracking #", token: "{{TRACKING_NUMBER}}" },
                { label: "Customer name", token: "{{CUSTOMER_NAME}}" },
              ].map((v) => (
                <button
                  key={v.token}
                  type="button"
                  onClick={() => insertAtCursor(v.token)}
                  className="px-2.5 py-1 rounded-full border border-violet-200 bg-violet-50 text-violet-700 text-[11px] font-medium hover:bg-violet-100 transition-colors"
                  data-testid={`designer-var-${v.token.replace(/[^a-z_]/gi, "")}`}
                >
                  + {v.label}
                </button>
              ))}
            </div>
            {forms.length > 0 && (
              <div className="mt-3" data-testid="designer-form-variables">
                <div className="text-[11px] font-medium text-zinc-500 mb-1">Insert a form — each recipient gets a personal link; answers land on their form page with contact info pre-filled</div>
                <div className="flex flex-wrap gap-1.5">
                  {forms.map((f) => (
                    <span key={f.id} className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 overflow-hidden">
                      <button type="button" onClick={() => insertAtCursor(`\n\n{{FORM_EMBED:${f.form_id}}}\n\n`)} className="px-2.5 py-1 text-emerald-700 text-[11px] font-medium hover:bg-emerald-100 transition-colors" title="Question + option cards inside the email" data-testid={`designer-form-embed-${f.form_id}`}>+ {f.name} · in-email form</button>
                      <button type="button" onClick={() => insertAtCursor(`\n\n{{FORM_BUTTON:${f.form_id}}}\n\n`)} className="px-2 py-1 border-l border-emerald-200 text-emerald-700 text-[11px] hover:bg-emerald-100 transition-colors" title="Just a button" data-testid={`designer-form-button-${f.form_id}`}>button</button>
                      <button type="button" onClick={() => insertAtCursor(`{{FORM_LINK:${f.form_id}}}`)} className="px-2 py-1 border-l border-emerald-200 text-emerald-700 text-[11px] hover:bg-emerald-100 transition-colors" title="Just the link URL" data-testid={`designer-form-link-${f.form_id}`}>link</button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="mt-3">
            <ImageUpload
              label="Add image (upload, URL, or your gallery)"
              value=""
              onChange={handlePickImage}
              shape="square"
              kind="gallery"
              gallerySource="airtable"
              userRecordId={userRecordId}
              testId="designer-image"
            />
          </div>

          {mode === "ai" && (
            <div className="mt-3 space-y-3">
              <div>
                <div className="text-xs font-medium text-zinc-600 mb-1">
                  Reference links (optional, up to 3) — AI reads these and pulls in the content &amp; images
                </div>
                {urls.map((u, i) => (
                  <Input key={i} value={u}
                    onChange={(e) => setUrls((prev) => prev.map((x, j) => j === i ? e.target.value : x))}
                    placeholder={`https://… ${i + 1}`}
                    className="h-9 rounded-lg text-sm mb-1.5"
                    data-testid={`designer-url-${i}`} />
                ))}
              </div>
              {openaiConnected && (
                <div className="flex items-center gap-2 text-xs flex-wrap">
                  <span className="text-zinc-500">AI engine:</span>
                  <div className="flex rounded-lg bg-zinc-100 p-0.5">
                    <button onClick={() => setProvider("emergent")}
                      className={`px-2.5 h-7 rounded-md font-medium ${provider === "emergent" ? "bg-white shadow-sm text-zinc-900" : "text-zinc-500"}`}
                      data-testid="designer-provider-emergent">OreVital AI (low cost)</button>
                    <button onClick={() => setProvider("openai")}
                      className={`px-2.5 h-7 rounded-md font-medium ${provider === "openai" ? "bg-white shadow-sm text-zinc-900" : "text-zinc-500"}`}
                      data-testid="designer-provider-openai">OpenAI (GPT-5.6)</button>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3 flex-wrap">
                <Button type="button" size="sm"
                  className="rounded-lg bg-violet-600 hover:bg-violet-700 text-white"
                  disabled={polishing} onClick={polish} data-testid="designer-polish">
                  {polishing ? <Loader2 className="animate-spin mr-1" size={14} /> : <Sparkles size={14} className="mr-1" />}
                  Polish with AI
                </Button>
                {points && (
                  <span className="text-xs text-zinc-500" data-testid="designer-ai-points">
                    <Sparkles size={11} className="inline mb-0.5 text-amber-500" />{" "}
                    <strong className="text-zinc-700">{points.balance}</strong> AI points
                    <span className="text-zinc-400"> · uses {1 + urls.filter((u) => u.trim()).length}</span>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        <div>
          <div className="text-xs font-medium text-zinc-600 mb-1 flex items-center gap-1">
            <Eye size={13} /> Live preview
          </div>
          <div className="rounded-2xl border border-zinc-200 overflow-hidden bg-white">
            <iframe title="designer-preview" sandbox=""
              srcDoc={`<!doctype html><html><head><meta name="viewport" content="width=device-width,initial-scale=1"></head><body style="margin:0">${previewHtml}</body></html>`}
              className="w-full" style={{ height: "48vh", border: 0 }}
              data-testid="designer-preview-frame" />
          </div>
        </div>

        <Button onClick={save} disabled={saving}
          className="rounded-xl h-11 w-full bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="designer-save-btn">
          {saving ? <Loader2 className="animate-spin mr-1" size={15} /> : <Save size={15} className="mr-1" />}
          Save email
        </Button>
        <p className="text-[11px] text-zinc-400 text-center">
          Saved private to you. Open it from your templates to Share it or assign specific dealers.
        </p>
      </div>
      )}
    </div>
  );
}
